import React, { useCallback, useEffect, useState } from 'react'
import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, OutlinedInput, Select, TextField, Typography } from '@mui/material'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import LockResetIcon from '@mui/icons-material/LockReset'
import Swal from 'sweetalert2'

import { getAlmacenes, getRolesYDepartamentos, getTiposMaquinas, getUsuariosAdmin, patchMiPassword, patchUsuario, patchUsuarioPassword, postUsuario } from '../services/apis'
import { UsuarioCard } from '../components/usuarios/UsuarioCard'
import { LoadingMaestro } from '../components/LoadingMaestro'
import { normalizarArray } from '../utils/apiResponse'

const getApiErrorMessage = (error, fallback) => {
    const message = error.response?.data?.message

    if (Array.isArray(message)) {
        return message.join('\n')
    }

    return message || fallback
}

const usuarioInicial = {
    usuario: '',
    nombreUsuario: '',
    password: '',
    idRol: '',
    activo: true,
    accesoGlobalAlmacenes: false,
    almacenes: [],
    accesoGlobalTiposMaquina: true,
    tiposMaquina: [],
}

const getTipoMaquinaCodigo = (tipoMaquina) => tipoMaquina?.codTipoMaquina ?? tipoMaquina?.tipoMaquina ?? tipoMaquina?.idTipoMaquina ?? tipoMaquina?.id

const getTipoMaquinaDescripcion = (tipoMaquina) => tipoMaquina?.desTipoMaquina ?? tipoMaquina?.desTipo ?? tipoMaquina?.descripcion ?? ''

const normalizarTiposMaquinaUsuario = (tiposMaquina = []) => (
    Array.isArray(tiposMaquina)
        ? tiposMaquina
            .map((tipoMaquina) => (typeof tipoMaquina === 'object' ? getTipoMaquinaCodigo(tipoMaquina) : tipoMaquina))
            .filter((tipoMaquina) => tipoMaquina !== undefined && tipoMaquina !== null && tipoMaquina !== '')
            .map(Number)
        : []
)

export const Usuarios = () => {
    const [usuarios, setUsuarios] = useState([])
    const [roles, setRoles] = useState([])
    const [almacenes, setAlmacenes] = useState([])
    const [tiposMaquinas, setTiposMaquinas] = useState([])
    const [loadingUsuarios, setLoadingUsuarios] = useState(true)
    const [openEditar, setOpenEditar] = useState(false)
    const [openPasswordUsuario, setOpenPasswordUsuario] = useState(false)
    const [openMiPassword, setOpenMiPassword] = useState(false)
    const [usuarioEditandoId, setUsuarioEditandoId] = useState(null)
    const [usuarioForm, setUsuarioForm] = useState(usuarioInicial)
    const [passwordUsuarioForm, setPasswordUsuarioForm] = useState({
        idUsuario: null,
        nombreUsuario: '',
        nuevaPassword: '',
    })
    const [miPasswordForm, setMiPasswordForm] = useState({
        passwordActual: '',
        nuevaPassword: '',
    })

    const cargarUsuarios = useCallback(async () => {
        setLoadingUsuarios(true)
        try {
            const response = await getUsuariosAdmin()
            const usuariosData = normalizarArray(response, 'usuarios')

            setUsuarios(usuariosData)
        } catch (error) {
            console.error("Error al cargar usuarios:", error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al cargar los usuarios'),
                timer: 2500,
                showConfirmButton: false,
            })
        } finally {
            setLoadingUsuarios(false)
        }
    }, [])

    const cargarMaestros = useCallback(async () => {
        try {
            const [rolesResponse, almacenesResponse, tiposMaquinasResponse] = await Promise.all([
                getRolesYDepartamentos(),
                getAlmacenes(),
                getTiposMaquinas(),
            ])

            setRoles(normalizarArray(rolesResponse, 'roles'))
            setAlmacenes(normalizarArray(almacenesResponse, 'almacenes'))
            setTiposMaquinas(normalizarArray(tiposMaquinasResponse, 'tiposMaquinas'))
        } catch (error) {
            console.error("Error al cargar maestros de usuarios:", error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al cargar roles, almacenes o tipos de maquina'),
                timer: 2500,
                showConfirmButton: false,
            })
        }
    }, [])

    useEffect(() => {
        cargarUsuarios()
        cargarMaestros()
    }, [cargarMaestros, cargarUsuarios])

    const abrirCrearUsuario = () => {
        setUsuarioForm(usuarioInicial)
        setUsuarioEditandoId(null)
        setOpenEditar(true)
    }

    const abrirEditarUsuario = (usuario) => {
        const tiposMaquinaUsuario = normalizarTiposMaquinaUsuario(usuario.tiposMaquina ?? usuario.tiposMaquinas)

        setUsuarioForm({
            idUsuario: usuario.idUsuario,
            usuario: usuario.usuario || '',
            nombreUsuario: usuario.nombreUsuario || '',
            password: '',
            idRol: usuario.idRol || '',
            activo: usuario.activo ?? true,
            accesoGlobalAlmacenes: usuario.accesoGlobalAlmacenes ?? false,
            almacenes: usuario.almacenes || [],
            accesoGlobalTiposMaquina: tiposMaquinaUsuario.length === 0,
            tiposMaquina: tiposMaquinaUsuario,
        })
        setUsuarioEditandoId(usuario.idUsuario)
        setOpenEditar(true)
    }

    const cerrarEditarUsuario = () => {
        setOpenEditar(false)
        setUsuarioEditandoId(null)
        setUsuarioForm(usuarioInicial)
    }

    const handleChangeUsuario = (event) => {
        const { name, value, checked, type } = event.target

        setUsuarioForm((usuarioActual) => ({
            ...usuarioActual,
            [name]: type === 'checkbox' ? checked : value,
            ...(name === 'accesoGlobalTiposMaquina' && checked ? { tiposMaquina: [] } : {}),
        }))
    }

    const handleChangeAlmacenesUsuario = (event) => {
        const { value } = event.target

        setUsuarioForm((usuarioActual) => ({
            ...usuarioActual,
            almacenes: typeof value === 'string' ? value.split(',') : value,
        }))
    }

    const handleChangeTiposMaquinaUsuario = (event) => {
        const { value } = event.target
        const tiposSeleccionados = typeof value === 'string' ? value.split(',') : value

        setUsuarioForm((usuarioActual) => ({
            ...usuarioActual,
            tiposMaquina: tiposSeleccionados.map(Number),
        }))
    }

    const guardarUsuario = async (event) => {
        event.preventDefault()

        if (!usuarioForm.accesoGlobalTiposMaquina && usuarioForm.tiposMaquina.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Selecciona tipos de maquina',
                text: 'Si el usuario no tiene acceso a todas las maquinas, debes seleccionar al menos un tipo.',
                timer: 2500,
                showConfirmButton: false,
            })
            return
        }

        const usuarioEnvio = {
            usuario: usuarioForm.usuario.trim(),
            nombreUsuario: usuarioForm.nombreUsuario.trim(),
            idRol: Number(usuarioForm.idRol),
            activo: usuarioForm.activo,
            accesoGlobalAlmacenes: usuarioForm.accesoGlobalAlmacenes,
            almacenes: usuarioForm.accesoGlobalAlmacenes ? [] : usuarioForm.almacenes,
            tiposMaquina: usuarioForm.accesoGlobalTiposMaquina ? [] : usuarioForm.tiposMaquina.map(Number),
        }

        try {
            const editandoUsuario = Boolean(usuarioEditandoId)
            if (usuarioEditandoId) {
                await patchUsuario(usuarioEditandoId, usuarioEnvio)
            } else {
                await postUsuario({
                    ...usuarioEnvio,
                    password: usuarioForm.password,
                })
            }
            cerrarEditarUsuario()
            cargarUsuarios()
            Swal.fire({
                icon: 'success',
                title: editandoUsuario ? 'Usuario actualizado' : 'Usuario creado',
                showConfirmButton: false,
                timer: 1500,
            })
        } catch (error) {
            console.error("Error al actualizar usuario:", error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, usuarioEditandoId ? 'Error al actualizar el usuario' : 'Error al crear el usuario'),
                timer: 2500,
                showConfirmButton: false,
            })
        }
    }

    const abrirPasswordUsuario = (usuario) => {
        setPasswordUsuarioForm({
            idUsuario: usuario.idUsuario,
            nombreUsuario: usuario.nombreUsuario || usuario.usuario || '',
            nuevaPassword: '',
        })
        setOpenPasswordUsuario(true)
    }

    const cerrarPasswordUsuario = () => {
        setOpenPasswordUsuario(false)
        setPasswordUsuarioForm({
            idUsuario: null,
            nombreUsuario: '',
            nuevaPassword: '',
        })
    }

    const guardarPasswordUsuario = async (event) => {
        event.preventDefault()

        try {
            await patchUsuarioPassword(passwordUsuarioForm.idUsuario, {
                nuevaPassword: passwordUsuarioForm.nuevaPassword,
            })
            cerrarPasswordUsuario()
            Swal.fire({
                icon: 'success',
                title: 'Contraseña actualizada',
                showConfirmButton: false,
                timer: 1500,
            })
        } catch (error) {
            console.error("Error al cambiar contraseña de usuario:", error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al cambiar la contraseña'),
                timer: 2500,
                showConfirmButton: false,
            })
        }
    }

    const cerrarMiPassword = () => {
        setOpenMiPassword(false)
        setMiPasswordForm({
            passwordActual: '',
            nuevaPassword: '',
        })
    }

    const guardarMiPassword = async (event) => {
        event.preventDefault()

        try {
            await patchMiPassword(miPasswordForm)
            cerrarMiPassword()
            Swal.fire({
                icon: 'success',
                title: 'Contraseña actualizada',
                showConfirmButton: false,
                timer: 1500,
            })
        } catch (error) {
            console.error("Error al cambiar mi contraseña:", error)
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: getApiErrorMessage(error, 'Error al cambiar tu contraseña'),
                timer: 2500,
                showConfirmButton: false,
            })
        }
    }

    return (
        <Grid
            container
            direction="row"
            size={{ xs: 12 }}
            sx={{
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <Grid
                container
                direction="row"
                size={{ xs: 12 }}
                sx={{
                    justifyContent: "center",
                    alignItems: "center",
                    mt: 3,
                }}
                className='fade-in ajustes-compact'
            >
                {loadingUsuarios ? (
                    <Grid container direction="row" size={{ xs: 10 }} sx={{ justifyContent: "center", alignItems: "center" }}>
                        <LoadingMaestro label="Cargando usuarios..." />
                    </Grid>
                ) : (
                <>
                <Grid
                    container
                    direction="row"
                    size={{ xs: 10 }}
                    sx={{
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                    spacing={1}
                >
                    <Typography variant='h4'>
                        Usuarios
                    </Typography>

                    <Grid
                        container
                        direction="row"
                        size={{ xs: 12, sm: 7, md: 6, lg: 5 }}
                        sx={{
                            justifyContent: { xs: "start", sm: "end" },
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Button variant="contained" onClick={abrirCrearUsuario}>
                            <AddCircleIcon />&nbsp;Nuevo usuario
                        </Button>

                        <Button variant="outlined" onClick={() => setOpenMiPassword(true)}>
                            <LockResetIcon />&nbsp;Cambiar mi contraseña
                        </Button>
                    </Grid>

                    <Divider />
                </Grid>

                <Grid
                    container
                    direction="row"
                    size={{ xs: 10 }}
                    sx={{
                        justifyContent: "center",
                        alignItems: "stretch",
                        p: 2,
                    }}
                    spacing={2}
                >
                    {usuarios.map((info, index) => (
                        <Grid
                            key={info.idUsuario ?? info.id ?? index}
                            container
                            direction="row"
                            size={{ xs: 12, md: 6, lg: 3 }}
                            sx={{
                                justifyContent: "center",
                                alignItems: "stretch",
                            }}
                        >
                            <UsuarioCard
                                info={info}
                                onEdit={abrirEditarUsuario}
                                onChangePassword={abrirPasswordUsuario}
                            />
                        </Grid>
                    ))}
                </Grid>
                </>
                )}
            </Grid>

            <Dialog open={openEditar} onClose={cerrarEditarUsuario} fullWidth maxWidth="md">
                <form onSubmit={guardarUsuario}>
                    <DialogTitle>
                        {usuarioEditandoId ? 'Editar usuario' : 'Nuevo usuario'}
                    </DialogTitle>

                    <DialogContent>
                        <Grid container direction="row" size={{ xs: 12 }} spacing={2} sx={{ pt: 1 }}>
                            <Grid container size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Usuario"
                                    name="usuario"
                                    value={usuarioForm.usuario}
                                    onChange={handleChangeUsuario}
                                    fullWidth
                                    required
                                />
                            </Grid>

                            <Grid container size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Nombre"
                                    name="nombreUsuario"
                                    value={usuarioForm.nombreUsuario}
                                    onChange={handleChangeUsuario}
                                    fullWidth
                                    required
                                />
                            </Grid>

                            {!usuarioEditandoId && (
                                <Grid container size={{ xs: 12, sm: 6 }}>
                                    <TextField
                                        label="Contraseña"
                                        name="password"
                                        type="password"
                                        value={usuarioForm.password}
                                        onChange={handleChangeUsuario}
                                        fullWidth
                                        required
                                    />
                                </Grid>
                            )}

                            <Grid container size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Rol</InputLabel>
                                    <Select
                                        label="Rol"
                                        name="idRol"
                                        value={usuarioForm.idRol}
                                        onChange={handleChangeUsuario}
                                        required
                                    >
                                        {roles.map((rol) => (
                                            <MenuItem key={rol.idRol ?? rol.id} value={rol.idRol ?? rol.id}>
                                                {rol.desRol || rol.nombre}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid container direction="column" size={{ xs: 12, sm: 6 }} sx={{ alignItems: "start" }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="activo"
                                            checked={usuarioForm.activo}
                                            onChange={handleChangeUsuario}
                                        />
                                    }
                                    label="Activo"
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="accesoGlobalAlmacenes"
                                            checked={usuarioForm.accesoGlobalAlmacenes}
                                            onChange={handleChangeUsuario}
                                        />
                                    }
                                    label="Acceso global a almacenes"
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            name="accesoGlobalTiposMaquina"
                                            checked={usuarioForm.accesoGlobalTiposMaquina}
                                            onChange={handleChangeUsuario}
                                        />
                                    }
                                    label="Acceso a todas las maquinas"
                                />
                            </Grid>

                            <Grid container size={{ xs: 12 }}>
                                <FormControl fullWidth disabled={usuarioForm.accesoGlobalAlmacenes}>
                                    <InputLabel>Almacenes</InputLabel>
                                    <Select
                                        multiple
                                        label="Almacenes"
                                        value={usuarioForm.almacenes}
                                        onChange={handleChangeAlmacenesUsuario}
                                        input={<OutlinedInput label="Almacenes" />}
                                        renderValue={(selected) => selected.join(', ')}
                                    >
                                        {almacenes.map((almacen) => (
                                            <MenuItem key={almacen.codAlmacen} value={almacen.codAlmacen}>
                                                <Checkbox checked={usuarioForm.almacenes.includes(almacen.codAlmacen)} />
                                                {almacen.codAlmacen} - {almacen.desAlmacen}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {!usuarioForm.accesoGlobalTiposMaquina && (
                                <Grid container size={{ xs: 12 }}>
                                    <FormControl fullWidth>
                                        <InputLabel>Tipos de maquina</InputLabel>
                                        <Select
                                            multiple
                                            label="Tipos de maquina"
                                            value={usuarioForm.tiposMaquina}
                                            onChange={handleChangeTiposMaquinaUsuario}
                                            input={<OutlinedInput label="Tipos de maquina" />}
                                            renderValue={(selected) => selected
                                                .map((codigoTipoMaquina) => {
                                                    const tipoMaquina = tiposMaquinas.find((tipo) => Number(getTipoMaquinaCodigo(tipo)) === Number(codigoTipoMaquina))
                                                    const descripcion = getTipoMaquinaDescripcion(tipoMaquina)

                                                    return descripcion ? `${codigoTipoMaquina} - ${descripcion}` : codigoTipoMaquina
                                                })
                                                .join(', ')}
                                        >
                                            {tiposMaquinas.map((tipoMaquina) => {
                                                const codigoTipoMaquina = getTipoMaquinaCodigo(tipoMaquina)

                                                return (
                                                    <MenuItem key={codigoTipoMaquina} value={Number(codigoTipoMaquina)}>
                                                        <Checkbox checked={usuarioForm.tiposMaquina.includes(Number(codigoTipoMaquina))} />
                                                        {codigoTipoMaquina} - {getTipoMaquinaDescripcion(tipoMaquina)}
                                                    </MenuItem>
                                                )
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Button color="primary" onClick={cerrarEditarUsuario}>
                            Cancelar
                        </Button>

                        <Button color="primary" variant="contained" type="submit">
                            {usuarioEditandoId ? 'Guardar usuario' : 'Crear usuario'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Dialog open={openPasswordUsuario} onClose={cerrarPasswordUsuario} fullWidth maxWidth="sm">
                <form onSubmit={guardarPasswordUsuario}>
                    <DialogTitle>
                        Cambiar contraseña
                    </DialogTitle>

                    <DialogContent>
                        <Grid container direction="column" size={{ xs: 12 }} spacing={2} sx={{ pt: 1 }}>
                            <Typography variant="body2">
                                Usuario: {passwordUsuarioForm.nombreUsuario}
                            </Typography>

                            <TextField
                                label="Nueva contraseña"
                                type="password"
                                value={passwordUsuarioForm.nuevaPassword}
                                onChange={(event) => setPasswordUsuarioForm((form) => ({ ...form, nuevaPassword: event.target.value }))}
                                fullWidth
                                required
                            />
                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Button color="primary" onClick={cerrarPasswordUsuario}>
                            Cancelar
                        </Button>

                        <Button color="primary" variant="contained" type="submit">
                            Guardar contraseña
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Dialog open={openMiPassword} onClose={cerrarMiPassword} fullWidth maxWidth="sm">
                <form onSubmit={guardarMiPassword}>
                    <DialogTitle>
                        Cambiar mi contraseña
                    </DialogTitle>

                    <DialogContent>
                        <Grid container direction="column" size={{ xs: 12 }} spacing={2} sx={{ pt: 1 }}>
                            <TextField
                                label="Contraseña actual"
                                type="password"
                                value={miPasswordForm.passwordActual}
                                onChange={(event) => setMiPasswordForm((form) => ({ ...form, passwordActual: event.target.value }))}
                                fullWidth
                                required
                            />

                            <TextField
                                label="Nueva contraseña"
                                type="password"
                                value={miPasswordForm.nuevaPassword}
                                onChange={(event) => setMiPasswordForm((form) => ({ ...form, nuevaPassword: event.target.value }))}
                                fullWidth
                                required
                            />
                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <Button color="primary" onClick={cerrarMiPassword}>
                            Cancelar
                        </Button>

                        <Button color="primary" variant="contained" type="submit">
                            Guardar contraseña
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </Grid>
    )
}
